import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useState } from "react";
import FirebaseContext from "../../../providers/Firebase/context";
import { EntryCodePreview } from "../../../model/EntryCodePreview";
import { ApiBackend } from "../../../providers/apibackend";

interface IProps {
    orderId: string;
    orderItemId: string;
    /** Visas i dialogen så admin ser vilken rad koden hamnar på */
    productName: string;
    participantName?: string;
    onAbort: () => void;
    onAdded: () => void;
}

const AddEntryCodeDialog = ({ orderId, orderItemId, productName, participantName, onAbort, onAdded }: IProps) => {
    const [code, setCode] = useState("");
    const [checking, setChecking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>(null);
    const [preview, setPreview] = useState<EntryCodePreview>(null);

    const backend = new ApiBackend();

    const handleCheck = async (): Promise<void> => {
        setError(null);
        setPreview(null);

        if (!code.trim()) {
            return;
        }

        setChecking(true);
        const res = await backend.previewEntryCode(orderId, { orderItemId, code: code.trim() });
        setChecking(false);

        if (res.status !== "OK" || !res.preview) {
            setError(res.message ?? "Koden är inte giltig");
            return;
        }

        setPreview(res.preview);
    };

    const handleAdd = async (user: any): Promise<void> => {
        setError(null);
        setSaving(true);

        const res = await backend.addEntryCode(orderId, {
            orderItemId,
            code: code.trim(),
            issuedBy: user?.email ?? ""
        });

        setSaving(false);

        if (res.status !== "OK") {
            setError(res.message ?? "Kunde inte lägga till koden");
            return;
        }

        onAdded();
    };

    const handleCodeChange = (value: string): void => {
        setCode(value);
        // Ny kod -> tidigare kontroll gäller inte längre
        setPreview(null);
        setError(null);
    };

    const busy = checking || saving;

    return (
        <FirebaseContext.Consumer>
            {({ user }: any) => (
                <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
                    <DialogTitle>Lägg till anmälningskod</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {productName}{participantName ? ` · ${participantName}` : ""}
                                </Typography>
                            </Grid>

                            <Grid item xs={8}>
                                <TextField
                                    label="Anmälningskod"
                                    fullWidth
                                    autoFocus
                                    InputLabelProps={{ shrink: true }}
                                    value={code}
                                    disabled={busy}
                                    onChange={(ev) => handleCodeChange(ev.target.value)}
                                    onKeyDown={(ev) => { if (ev.key === "Enter") { handleCheck(); } }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                    disabled={busy || !code.trim()}
                                    onClick={handleCheck}
                                >
                                    {checking ? <CircularProgress size={20} /> : "Kontrollera"}
                                </Button>
                            </Grid>

                            {error &&
                                <Grid item xs={12}>
                                    <Alert severity="error">{error}</Alert>
                                </Grid>
                            }

                            {preview && <>
                                <Grid item xs={12}>
                                    <Alert severity="success">
                                        Giltig kod: <b>{preview.codeHolderName}</b>
                                    </Alert>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 16px" }}>
                                        <Typography variant="body2">
                                            Rabatt: <b>−{preview.discount} kr</b>
                                        </Typography>
                                        <Typography variant="body2">
                                            Radpris: {preview.currentItemPrice} kr → <b>{preview.newItemPrice} kr</b>
                                        </Typography>
                                        <Typography variant="body2">
                                            Ordersumma: {preview.currentTotalSum} kr → <b>{preview.newTotalSum} kr</b>
                                        </Typography>
                                    </Box>
                                </Grid>
                                {preview.overpaidAfter > 0 &&
                                    <Grid item xs={12}>
                                        <Alert severity="warning">
                                            Ordern blir <b>överbetald med {preview.overpaidAfter} kr</b> (inbetalt {preview.totalPaid} kr).
                                            Ingen återbetalning görs automatiskt — hantera det separat via Återbetala.
                                        </Alert>
                                    </Grid>
                                }
                            </>}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={busy || !preview}
                            onClick={() => handleAdd(user)}
                        >
                            {saving ? <CircularProgress size={20} /> : "Lägg till kod"}
                        </Button>
                        <Button variant="contained" color="secondary" disabled={busy} onClick={onAbort}>Avbryt</Button>
                    </DialogActions>
                </Dialog>
            )}
        </FirebaseContext.Consumer>
    );
};

export default AddEntryCodeDialog;
